<template>

  <panel-navigation :expandable="true" :customPadding="'20px 30px'" :mobileEnabled="true">

    <!-- Unsaved Changes Dialog -->
    <unsaved-changes-dialog
      v-model="showUnsavedDialog"
      @cancel="cancelNavigation"
      @save-and-continue="saveAndNavigate"
      @close="cancelNavigation"
    />

    <!-- Disconnect Confirmation Dialog -->
    <disconnect-confirm-dialog
      v-model="showDisconnectDialog"
      :service-name="disconnectServiceName"
      :loading="disconnectLoading"
      @confirm="confirmDisconnect"
      @cancel="cancelDisconnect"
    />

    <h1 class="text-lg font-semibold mb-4">Settings</h1>
    <hr class="border-b border-gray-100 mb-3">

    <div v-if="me">

      <div class="dashboard-box">
        <v-card
          class="pa-2"
          tile
        >
          <div class="p-3 pt-1">
            <v-tabs v-model="currentTab" @change="onTabChange">
              <v-tab>Profile</v-tab>
              <v-tab>Change Password</v-tab>
              <v-tab>Billing</v-tab>
              <v-tab>Integrations</v-tab>

              <v-tab-item class="pt-5">
                <div class="flex flew-row flex-wrap">
                  <div class="w-full sm:w-1/4 pr-6 mb-4 mt-2" style="text-align: center;">
                    <v-gravatar v-if="profile" class="gravatar-pic" :email="profile.email" default-img="https://regro-cdn.s3.amazonaws.com/images/profile_image.png" />
                    <br />
                    <strong v-if="profile">{{ profile.first_name }} {{ profile.last_name }}</strong>
                  </div>
                  <div class="w-full sm:w-3/4 mb-4" v-if="profile">
                    <div class="flex flew-row flex-wrap">
                      <div class="w-full sm:w-1/2 pr-6 mb-4">
                        <p class="mb-1 text-xs mb-2"><strong>First Name</strong></p>
                        <input type="text" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" v-model="profile.first_name" />
                      </div>
                      <div class="w-full sm:w-1/2 mb-4">
                        <p class="mb-1 text-xs mb-2"><strong>Last Name</strong></p>
                        <input type="text" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" v-model="profile.last_name" />
                      </div>
                      <div class="w-full sm:w-1/2 pr-6 mb-4">
                        <p class="mb-1 text-xs mb-2"><strong>Email</strong></p>
                        <input type="text" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" v-model="profile.email" />
                      </div>
                      <div class="w-full sm:w-1/2 mb-4">
                        <p class="mb-1 text-xs mb-2"><strong>Phone</strong></p>
                        <input
                          type="tel"
                          class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded"
                          v-model="profile.phone"
                          @input="formatPhoneNumber"
                          placeholder="+1 234 567 8900" />
                        <p v-if="phoneError" class="text-red-500 text-xs mt-1">{{ phoneError }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="mt-3" />

                <div>
                  <div v-if="successMessage" class="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4" role="alert">
                    {{ successMessage }}
                  </div>
                  <div v-if="error" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
                    {{ error }}
                  </div>
                  <div class="bg-blue-500 float-right hover:bg-blue-400 text-white text-sm inline-block rounded py-2 px-3 cursor-pointer mt-6" :class="{'spinner': saving}" @click="save()">Save</div>
                </div>
              </v-tab-item>

              <v-tab-item class="pt-5">
                <div class="w-full sm:w-1/3 pr-6 mb-4">
                  <p class="mb-1 text-xs mb-2"><strong>Current Password</strong></p>
                  <input type="password" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" ref="current_password" @input="trackPasswordChanges" />
                </div>
                <div class="w-full sm:w-1/3 pr-6 mb-4">
                  <p class="mb-1 text-xs mb-2"><strong>New Password</strong></p>
                  <input type="password" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" ref="new_password" @input="trackPasswordChanges" />
                </div>
                <div class="w-full sm:w-1/3 pr-6 mb-4">
                  <p class="mb-1 text-xs mb-2"><strong>Confirm Password</strong></p>
                  <input type="password" class="rg-text-field block appearance-none focus:outline-none focus:border-gray-500 w-full bg-white hover:border-grey px-2 py-2 rounded" ref="confirm_password" @input="trackPasswordChanges" />
                </div>

                <div v-if="error_password" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
                  <div v-html="error_password"></div>
                </div>

                <hr class="mt-3" />

                <div class="float-right mt-6">
                  <v-chip
                    v-if="password_changed"
                    class="ma-2 mt-2 ml-3"
                    color="green"
                    outlined
                    @click="password_changed = false"
                    style="position: absolute;"
                  >
                    <v-icon left>
                      mdi-check
                    </v-icon>
                    Password changed!
                  </v-chip>

                  <div class="bg-blue text-white text-sm inline-block rounded py-2 px-3 cursor-pointer" :class="{'spinner': changing}" @click="change_password">Change Password</div>
                </div>
              </v-tab-item>

              <!-- Billing Tab -->
              <v-tab-item class="pt-3">
                <div class="billing-page">
                  <!-- Subscription Required Alert -->
                  <div v-if="showSubscriptionAlert" class="subscription-alert">
                    <div class="alert-header">
                      <i class="fas fa-exclamation-triangle"></i>
                      <h2>Subscription Required</h2>
                    </div>
                    <p>To continue using Grid Rank, you need an active subscription. Please choose a plan below to get started.</p>
                    <div class="alert-actions">
                      <button @click="showPlanOptions = true" class="view-plans-btn">
                        View Available Plans
                      </button>
                    </div>
                  </div>
                  <!-- Top Cards Section -->
                  <div class="top-cards-row">
                    <!-- Current Plan Card -->
                    <div class="plan-card">
                      <div class="card-header-row">
                        <div class="header-left">
                          <span class="card-title">Current Plan</span>
                          <a href="#" @click.prevent="showPlanOptions = !showPlanOptions" class="upgrade-link">Upgrade</a>
                        </div>
                        <a href="#" @click.prevent="cancelSubscription" :disabled="!subscription || subscription.status !== 'active'" class="cancel-link">Cancel Plan</a>
                      </div>

                      <div v-if="subscription" class="plan-body">
                        <div class="plan-name">{{ getPlanDisplayName() }}</div>
                        <div class="plan-status-row">
                          Status : <span class="yes-label">{{ subscription.status === 'active' ? 'Active' : subscription.status }}</span>
                        </div>
                        <div class="plan-price">{{ getPlanPrice() }}</div>
                        <div class="next-payment-row">
                          Next Payment : <span class="payment-date"> {{ formatNextPayment(subscription.current_period_end) }}</span>
                        </div>
                      </div>
                      <div v-else class="plan-body">
                        <div class="plan-name">No active subscription</div>
                        <button @click="showPlanOptions = true" class="activate-btn">
                          <i class="fas fa-plus-circle"></i> Activate Subscription
                        </button>
                      </div>

                      <!-- Plan Options (shown when Upgrade is clicked) -->
                      <div v-if="showPlanOptions" class="plan-options">
                        <!-- Monthly Plan -->
                        <div class="plan-option" :class="{ 'active-plan': subscription && subscription.plan_type === 'monthly' }">
                          <div class="plan-option-header">
                            <div>
                              <h4>Monthly Plan</h4>
                              <p class="plan-option-price">$10/month</p>
                            </div>
                            <div>
                              <span v-if="subscription && subscription.plan_type === 'monthly'" class="current-plan-badge">Current Plan</span>
                              <button v-else @click="changePlanType('monthly')" :disabled="changingPlan" class="switch-plan-btn">
                                {{ changingPlan ? 'Processing...' : (subscription ? 'Switch to Monthly' : 'Get Monthly Plan') }}
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Yearly Plan -->
                        <div class="plan-option" :class="{ 'active-plan': subscription && subscription.plan_type === 'yearly' }">
                          <div class="plan-option-header">
                            <div>
                              <h4>Yearly Plan</h4>
                              <p class="plan-option-price">$120/year</p>
                            </div>
                            <div>
                              <span v-if="subscription && subscription.plan_type === 'yearly'" class="current-plan-badge">Current Plan</span>
                              <button v-else @click="changePlanType('yearly')" :disabled="changingPlan" class="switch-plan-btn">
                                {{ changingPlan ? 'Processing...' : (subscription ? 'Switch to Yearly' : 'Get Yearly Plan') }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Payment Method Card -->
                    <div class="payment-card">
                      <div class="card-header-row">
                        <div class="header-left">
                          <span class="card-title">Payment Method</span>
                          <a href="#" @click.prevent="openCustomerPortal" class="edit-link">Edit</a>
                        </div>
                      </div>

                      <div v-if="paymentMethod" class="payment-body">
                        <div class="card-info-row">
                          <img :src="getCardIcon(paymentMethod.card_brand)" :alt="paymentMethod.card_brand" class="card-logo" />
                          <span class="card-number">{{ formatCardBrand(paymentMethod.card_brand) }} ending ** {{ paymentMethod.last4 || '1234' }}</span>
                        </div>
                        <div class="card-expiry">Expiry {{ paymentMethod.exp_month || '02' }}/{{ paymentMethod.exp_year || '2024' }}</div>
                      </div>
                      <div v-else class="payment-body">
                        <div class="no-payment">No payment method on file</div>
                      </div>
                    </div>
                  </div>
                  <h3 class="history-title">Billing History</h3>
                  <!-- Billing History Section -->
                  <div class="billing-history-card">
                   

                    <!-- Search and Filter Row -->
                    <div class="controls-row">
                      <div class="search-box">
                        <i class="fas fa-search"></i>
                        <input
                          v-model="invoiceSearch"
                          type="text"
                          placeholder="Search by invoice id"
                          @input="filterInvoices"
                        />
                      </div>
                      <select v-model="selectedMonth" @change="filterInvoices" class="month-filter">
                        <option value="">Filter by month</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>

                    <!-- Table -->
                    <div class="history-table-wrapper">
                    <table class="history-table">
                      <thead>
                        <tr>
                          <th>
                            Date
                            <i class="fas fa-sort"></i>
                          </th>
                          <th>
                            ID
                            <i class="fas fa-sort"></i>
                          </th>
                          <th>
                            Items
                            <i class="fas fa-sort"></i>
                          </th>
                          <th>
                            Amount
                            <i class="fas fa-sort"></i>
                          </th>
                          <th>
                            Status
                            <i class="fas fa-sort"></i>
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="loadingInvoices">
                          <td colspan="6" class="text-center">
                            <i class="fa fa-spinner fa-spin"></i> Loading invoices...
                          </td>
                        </tr>
                        <tr v-else-if="paginatedInvoices.length === 0">
                          <td colspan="6" class="text-center">No invoices found</td>
                        </tr>
                        <template v-else>
                          <tr v-for="invoice in paginatedInvoices" :key="invoice.id">
                            <td>{{ formatInvoiceDate(invoice.createdAt || invoice.date) }}</td>
                            <td>{{ invoice.invoice_number || invoice.id || '-' }}</td>
                            <td>{{ getInvoiceDescription(invoice) }}</td>
                            <td>{{ formatInvoiceAmount(invoice) }}</td>
                            <td>
                              <span class="status-badge" :class="getInvoiceStatusClass(invoice.status)">
                                {{ formatInvoiceStatus(invoice.status) }}
                              </span>
                            </td>
                            <td>
                              <div class="action-buttons">
                                <!-- <button @click="viewInvoice(invoice)" class="view-btn" title="View Invoice">
                                  <i class="fas fa-eye"></i>
                                </button> -->
                                  <span @click="downloadInvoice(invoice)">
                                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M23.75 18.2812V21.9271C23.75 22.4106 23.5451 22.8742 23.1805 23.2161C22.8158 23.5579 22.3213 23.75 21.8056 23.75H8.19444C7.67875 23.75 7.18417 23.5579 6.81951 23.2161C6.45486 22.8742 6.25 22.4106 6.25 21.9271V18.2812" stroke="#3A9BDB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                      <path d="M9.53125 11.7187L15 17.1875L20.4688 11.7188" stroke="#3A9BDB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                      <path d="M15 17.1875L15 6.25" stroke="#3A9BDB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  </span>

                              </div>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                    </div>

                    <!-- Pagination -->
                    <div class="pagination-row">
                      <div class="show-entries">
                        Show
                        <select v-model="itemsPerPage" @change="updatePagination" class="entries-select">
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                        </select>
                        Entries
                      </div>
                      <div class="page-info">
                        {{ currentPage * itemsPerPage > filteredInvoices.length ? filteredInvoices.length : currentPage * itemsPerPage }} of {{ filteredInvoices.length }}
                      </div>
                      <div class="page-controls">
                        <button @click="previousPage" :disabled="currentPage === 1" class="page-btn">
                          <i class="fas fa-chevron-left"></i>
                        </button>
                        <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-btn">
                          <i class="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <!-- Integrations Tab -->
              <v-tab-item class="pt-3">
                <div class="integrations-page">
                  <h3 class="connected-apps-title">Connected Apps</h3>

                  <!-- Google Maps Section -->
                  <div class="integration-card google-maps-card">
                    <div class="integration-header">
                      <div class="integration-title-section">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="40" height="40" rx="10" fill="#2477F6" fill-opacity="0.1"/>
                          <path d="M20 14.9395C21.8546 14.9395 23.3701 16.4466 23.3701 18.3096C23.3701 20.1708 21.8563 21.6699 20 21.6699C18.1454 21.6699 16.6301 20.1627 16.6299 18.2998C16.6299 16.4385 18.1436 14.9395 20 14.9395ZM20 15.4395C18.4252 15.4395 17.1299 16.7221 17.1299 18.3096C17.1299 19.8957 18.4139 21.1797 20 21.1797C21.586 21.1796 22.8701 19.8956 22.8701 18.3096C22.8701 16.7221 21.5747 15.4395 20 15.4395Z" fill="#2477F6" stroke="#2477F6"/>
                          <path d="M19.9997 30.76C18.5197 30.76 17.0297 30.2 15.8697 29.09C12.9197 26.25 9.65969 21.72 10.8897 16.33C11.9997 11.44 16.2697 9.25 19.9997 9.25C19.9997 9.25 19.9997 9.25 20.0097 9.25C23.7397 9.25 28.0097 11.44 29.1197 16.34C30.3397 21.73 27.0797 26.25 24.1297 29.09C22.9697 30.2 21.4797 30.76 19.9997 30.76ZM19.9997 10.75C17.0897 10.75 13.3497 12.3 12.3597 16.66C11.2797 21.37 14.2397 25.43 16.9197 28C18.6497 29.67 21.3597 29.67 23.0897 28C25.7597 25.43 28.7197 21.37 27.6597 16.66C26.6597 12.3 22.9097 10.75 19.9997 10.75Z" fill="#2477F6"/>
                        </svg>

                        <div>
                          <h4 class="integration-title">Google Cloud</h4>
                          <v-chip
                            v-if="loadingGoogleMaps"
                            small
                            color="blue lighten-5"
                            text-color="blue"
                            class="status-chip"
                          >
                            <i class="fas fa-spinner fa-spin mr-1"></i>
                            Loading...
                          </v-chip>
                          <v-chip
                            v-else-if="googleMapsStatus === 'connected'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="status-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected
                          </v-chip>
                          <v-chip
                            v-else-if="googleMapsStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="status-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Connected - API Not Working
                          </v-chip>
                          <v-chip
                            v-else
                            small
                            color="grey lighten-3"
                            text-color="grey darken-2"
                            class="status-chip"
                          >
                            <i class="fas fa-exclamation-circle mr-1"></i>
                            Not Connected
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <div class="integration-description">
                      <div class="description-content">
                        <div class="description-text">
                          <p class="text-sm text-gray-600">Google Maps is used for finding businesses using your search criteria. API's needed: Geocoding API and Places API</p>
                          <ul class="integration-features">
                            <li>• Please make sure billing is enabled in your Google Maps account before adding your key.</li>
                            <li>• Google offers $200 in free monthly credits. A search will cost on average around $0.25. It may cost more for Multi-Snipe searches.</li>
                            <li>• If you start going over your $200/mo in free usage we'd recommend using ValueSerp.</li>
                          </ul>
                        </div>
                        <div class="integration-actions">
                          <button @click="openGoogleMapsDialog" class="connect-btn">
                            {{ googleMapsApiKey ? 'UPDATE' : 'CONNECT' }}
                          </button>
                          <button class="tutorial-btn">
                            WATCH TUTORIAL
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Value Serp Section -->
                  <div v-if="useValueSerp" class="integration-card value-serp-card">
                    <div class="integration-header">
                      <div class="integration-title-section">
                       <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="40" height="40" rx="10" fill="#2477F6" fill-opacity="0.1"/>
                          <g clip-path="url(#clip0_388_6758)">
                            <path d="M23.6515 22.665C23.7491 22.5674 23.9074 22.5674 24.005 22.665L28.6012 27.2612C28.6988 27.3588 28.6988 27.5171 28.6012 27.6148C28.5036 27.7124 28.3453 27.7124 28.2477 27.6148L23.6515 23.0186C23.5538 22.9209 23.5538 22.7627 23.6515 22.665Z" fill="#2477F6" stroke="#2477F6"/>
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M26 18C26 14.134 22.866 11 19 11C15.134 11 12 14.134 12 18C12 21.866 15.134 25 19 25C22.866 25 26 21.866 26 18ZM24.5 18C24.5 14.9624 22.0376 12.5 19 12.5C15.9624 12.5 13.5 14.9624 13.5 18C13.5 21.0376 15.9624 23.5 19 23.5C22.0376 23.5 24.5 21.0376 24.5 18Z" fill="#2477F6"/>
                          </g>
                          <defs>
                            <clipPath id="clip0_388_6758">
                            <rect width="24" height="24" fill="white" transform="translate(8 8)"/>
                            </clipPath>
                          </defs>
                        </svg>
                        <div>
                          <h4 class="integration-title">Value Serp</h4>
                          <v-chip
                            v-if="loadingValueSerp"
                            small
                            color="blue lighten-5"
                            text-color="blue"
                            class="status-chip"
                          >
                            <i class="fas fa-spinner fa-spin mr-1"></i>
                            Loading...
                          </v-chip>
                          <v-chip
                            v-else-if="valueSerpStatus === 'connected'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="status-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected
                          </v-chip>
                          <v-chip
                            v-else-if="valueSerpStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="status-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Connected - API Not Working
                          </v-chip>
                          <v-chip
                            v-else
                            small
                            color="grey lighten-3"
                            text-color="grey darken-2"
                            class="status-chip"
                          >
                            <i class="fas fa-exclamation-circle mr-1"></i>
                            Not Connected
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <div class="integration-description">
                      <div class="description-content">
                        <div class="description-text">
                          <p class="text-sm text-gray-600">ValueSerp is used as an alternative for Google Maps API (optional).</p>
                          <ul class="integration-features">
                            <li>• ValueSerp costs $1.50 per 1,000 requests.</li>
                            <li>• We suggest integrating ValueSerp if you go over $200 usage in Google credits.</li>
                          </ul>
                          <div class="sab-search-section">
                            <label class="sab-toggle">
                              <span>SAB Search</span>
                              <input type="checkbox" v-model="sabSearchEnabled" @change="updateSabSearch">
                              <span class="toggle-slider"></span>
                            </label>
                            <p class="sab-description">When enabled we will use the rank tracker api to find the business, and identify CID so we can track its rankings.</p>
                          </div>
                        </div>
                        <div class="integration-actions">
                          <button @click="openValueSerpDialog" class="connect-btn">
                            {{ valueSerpApiKey ? 'UPDATE' : 'CONNECT' }}
                          </button>
                          <button class="tutorial-btn">
                            WATCH TUTORIAL
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- DataForSeo Section -->
                  <div class="integration-card dataforseo-card">
                    <div class="integration-header">
                      <div class="integration-title-section">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="40" height="40" rx="10" fill="#2477F6" fill-opacity="0.1"/>
                          <path d="M20 10C14.4772 10 10 14.4772 10 20C10 25.5228 14.4772 30 20 30C25.5228 30 30 25.5228 30 20C30 14.4772 25.5228 10 20 10ZM20 11.5C24.6944 11.5 28.5 15.3056 28.5 20C28.5 24.6944 24.6944 28.5 20 28.5C15.3056 28.5 11.5 24.6944 11.5 20C11.5 15.3056 15.3056 11.5 20 11.5Z" fill="#2477F6"/>
                          <path d="M20 14C19.4477 14 19 14.4477 19 15V20.5L22.5 24C22.8905 24.3905 23.5237 24.3905 23.9142 24C24.3047 23.6095 24.3047 22.9763 23.9142 22.5858L21 19.6716V15C21 14.4477 20.5523 14 20 14Z" fill="#2477F6"/>
                        </svg>
                        <div>
                          <h4 class="integration-title">DataForSeo</h4>
                          <v-chip
                            v-if="loadingDataForSeo"
                            small
                            color="blue lighten-5"
                            text-color="blue"
                            class="status-chip"
                          >
                            <i class="fas fa-spinner fa-spin mr-1"></i>
                            Loading...
                          </v-chip>
                          <v-chip
                            v-else-if="dataForSeoStatus === 'connected'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="status-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected
                          </v-chip>
                          <v-chip
                            v-else-if="dataForSeoStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="status-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Connected - API Not Working
                          </v-chip>
                          <v-chip
                            v-else
                            small
                            color="grey lighten-3"
                            text-color="grey darken-2"
                            class="status-chip"
                          >
                            <i class="fas fa-exclamation-circle mr-1"></i>
                            Not Connected
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <div class="integration-description">
                      <div class="description-content">
                        <div class="description-text">
                          <p class="text-sm text-gray-600">DataForSeo provides comprehensive SEO data and SERP analysis for advanced rank tracking and competitor analysis.</p>
                          <ul class="integration-features">
                            <!-- <li>• Use Base64 encoded credentials (username:password format)</li> -->
                            <li>• Provides real-time SERP data from multiple search engines</li>
                            <li>• Advanced filtering and location-based search capabilities</li>
                          </ul>

                          <!-- Search Toggle (only show when ValueSerp is disabled) -->
                          <!-- Toggle section commented out as requested -->
                          <!-- <div v-if="!useValueSerp" class="search-toggle-section">
                            <label class="toggle-container">
                              <input
                                type="checkbox"
                                v-model="dataForSeoSearchEnabled"
                                @change="toggleDataForSeoSearch"
                                :disabled="togglingDataForSeoSearch"
                              />
                              <span class="toggle-slider-small"></span>
                              <span class="toggle-text">Enable SAB (Service Area Business) Search</span>
                            </label>
                            <p class="toggle-hint">When enabled, allows searching for Service Area Businesses that don't have physical addresses</p>
                          </div> -->
                        </div>
                        <div class="integration-actions">
                          <button @click="openDataForSeoDialog" class="connect-btn">
                            {{ dataForSeoUsername ? 'UPDATE' : 'CONNECT' }}
                          </button>
                          <button class="tutorial-btn">
                            WATCH TUTORIAL
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Google Maps Dialog -->
                  <v-dialog v-model="googleMapsDialog" max-width="700px">
                    <v-card class="integration-dialog">
                      <v-card-title class="dialog-header">
                        <span>Google Map Settings</span>
                        <v-btn icon @click="googleMapsDialog = false" dark>
                          <v-icon color="white">mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <div class="dialog-content">
                          <label class="api-label">API Key</label>
                          <div class="api-input-wrapper">
                            <input
                              :type="showGoogleMapsKey ? 'text' : 'password'"
                              v-model="tempGoogleMapsKey"
                              class="api-input"
                              placeholder="Enter your Google Maps API key"
                            />
                            <button @click="showGoogleMapsKey = !showGoogleMapsKey" class="eye-btn">
                              <i :class="showGoogleMapsKey ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                            </button>
                          </div>

                          <v-chip
                            v-if="googleMapsValidationStatus === 'valid'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="validation-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected - API Working
                          </v-chip>
                          <v-chip
                            v-else-if="googleMapsValidationStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="validation-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Invalid - API Not Working
                          </v-chip>
                        </div>
                      </v-card-text>
                      <v-card-actions class="dialog-actions">
                        <button @click="disconnectGoogleMaps" class="disconnect-btn" v-if="googleMapsApiKey" :disabled="disconnectingGoogleMaps || savingGoogleMaps || validatingGoogleMaps">
                          <i v-if="disconnectingGoogleMaps" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ disconnectingGoogleMaps ? 'DISCONNECTING...' : 'DISCONNECT' }}
                        </button>
                        <!-- Revalidate button commented out as requested -->
                        <!-- <button @click="revalidateGoogleMaps" class="revalidate-btn" :disabled="!tempGoogleMapsKey || validatingGoogleMaps || savingGoogleMaps || disconnectingGoogleMaps">
                          <i v-if="validatingGoogleMaps" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ validatingGoogleMaps ? 'VALIDATING...' : 'REVALIDATE' }}
                        </button> -->
                        <button @click="updateGoogleMapsKey" class="update-btn" :disabled="!tempGoogleMapsKey || savingGoogleMaps || validatingGoogleMaps || disconnectingGoogleMaps">
                          <i v-if="savingGoogleMaps" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ savingGoogleMaps ? 'SAVING...' : (googleMapsApiKey ? 'UPDATE' : 'SUBMIT') }}
                        </button>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- Value Serp Dialog -->
                  <v-dialog v-model="valueSerpDialog" max-width="700px">
                    <v-card class="integration-dialog">
                      <v-card-title class="dialog-header">
                        <span>Value Serp Settings</span>
                        <v-btn icon @click="valueSerpDialog = false" dark>
                          <v-icon color="white">mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <div class="dialog-content">
                          <label class="api-label">API Key</label>
                          <div class="api-input-wrapper">
                            <input
                              :type="showValueSerpKey ? 'text' : 'password'"
                              v-model="tempValueSerpKey"
                              class="api-input"
                              placeholder="Enter your Value Serp API key"
                            />
                            <button @click="showValueSerpKey = !showValueSerpKey" class="eye-btn">
                              <i :class="showValueSerpKey ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                            </button>
                          </div>

                          <v-chip
                            v-if="valueSerpValidationStatus === 'valid'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="validation-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected - API Working
                          </v-chip>
                          <v-chip
                            v-else-if="valueSerpValidationStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="validation-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Invalid - API Not Working
                          </v-chip>
                        </div>
                      </v-card-text>
                      <v-card-actions class="dialog-actions">
                        <button @click="disconnectValueSerp" class="disconnect-btn" v-if="valueSerpApiKey" :disabled="disconnectingValueSerp || savingValueSerp || validatingValueSerp">
                          <i v-if="disconnectingValueSerp" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ disconnectingValueSerp ? 'DISCONNECTING...' : 'DISCONNECT' }}
                        </button>
                        <button @click="revalidateValueSerp" class="revalidate-btn" :disabled="!tempValueSerpKey || validatingValueSerp || savingValueSerp || disconnectingValueSerp">
                          <i v-if="validatingValueSerp" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ validatingValueSerp ? 'VALIDATING...' : 'REVALIDATE' }}
                        </button>
                        <button @click="updateValueSerpKey" class="update-btn" :disabled="!tempValueSerpKey || savingValueSerp || validatingValueSerp || disconnectingValueSerp">
                          <i v-if="savingValueSerp" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ savingValueSerp ? 'SAVING...' : (valueSerpApiKey ? 'UPDATE' : 'SUBMIT') }}
                        </button>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- DataForSeo Dialog -->
                  <v-dialog v-model="dataForSeoDialog" max-width="700px">
                    <v-card class="integration-dialog">
                      <v-card-title class="dialog-header">
                        <span>DataForSeo Settings</span>
                        <v-btn icon @click="dataForSeoDialog = false" dark>
                          <v-icon color="white">mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <div class="dialog-content">
                          <label class="api-label">Username (Email)</label>
                          <div class="api-input-wrapper">
                            <input
                              type="text"
                              :value="tempDataForSeoUsername"
                              @input="tempDataForSeoUsername = $event.target.value"
                              class="api-input"
                              placeholder="Enter your DataForSeo username/email"
                            />
                          </div>

                          <label class="api-label mt-3">Password</label>
                          <div class="api-input-wrapper">
                            <input
                              :type="showDataForSeoPassword ? 'text' : 'password'"
                              :value="tempDataForSeoPassword"
                              @input="tempDataForSeoPassword = $event.target.value"
                              class="api-input"
                              placeholder="Enter your DataForSeo password"
                            />
                            <button @click="showDataForSeoPassword = !showDataForSeoPassword" class="eye-btn">
                              <i :class="showDataForSeoPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                            </button>
                          </div>
                          <p class="text-xs text-gray-500 mt-2">
                            Enter your DataForSeo account credentials
                          </p>

                          <v-chip
                            v-if="dataForSeoValidationStatus === 'valid'"
                            small
                            color="green lighten-5"
                            text-color="green"
                            class="validation-chip"
                          >
                            <i class="fas fa-check-circle mr-1"></i>
                            Connected - API Working
                          </v-chip>
                          <v-chip
                            v-else-if="dataForSeoValidationStatus === 'invalid'"
                            small
                            color="red lighten-5"
                            text-color="red"
                            class="validation-chip"
                          >
                            <i class="fas fa-times-circle mr-1"></i>
                            Invalid - API Not Working
                          </v-chip>
                        </div>
                      </v-card-text>
                      <v-card-actions class="dialog-actions">
                        <button @click="disconnectDataForSeo" class="disconnect-btn" v-if="dataForSeoUsername" :disabled="disconnectingDataForSeo || savingDataForSeo || validatingDataForSeo">
                          <i v-if="disconnectingDataForSeo" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ disconnectingDataForSeo ? 'DISCONNECTING...' : 'DISCONNECT' }}
                        </button>
                        <!-- Revalidate button commented out as requested -->
                        <!-- <button @click="revalidateDataForSeo" class="revalidate-btn" :disabled="(!tempDataForSeoUsername || !tempDataForSeoPassword) || validatingDataForSeo || savingDataForSeo || disconnectingDataForSeo">
                          <i v-if="validatingDataForSeo" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ validatingDataForSeo ? 'VALIDATING...' : 'REVALIDATE' }}
                        </button> -->
                        <button @click="updateDataForSeoKey" class="update-btn" :disabled="(!tempDataForSeoUsername || !tempDataForSeoPassword) || savingDataForSeo || validatingDataForSeo || disconnectingDataForSeo">
                          <i v-if="savingDataForSeo" class="fas fa-spinner fa-spin mr-1"></i>
                          {{ savingDataForSeo ? 'SAVING...' : (dataForSeoUsername ? 'UPDATE' : 'SUBMIT') }}
                        </button>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </div>
              </v-tab-item>
            </v-tabs>
          </div>
        </v-card>
      </div>

    </div>



  </panel-navigation>

</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import PanelNavigation from '@/components/PanelNavigation.vue'
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog.vue'
import DisconnectConfirmDialog from '@/components/DisconnectConfirmDialog.vue'
import { parsePhoneNumber, AsYouType, isValidPhoneNumber } from 'libphonenumber-js'

export default {
  metaInfo () {
    return {
      title: "Settings | " + this.$appName
    }
  },
  components: {
    PanelNavigation,
    UnsavedChangesDialog,
    DisconnectConfirmDialog
  },
  data() {
    return {
      userSegmentation: false,
      saving: false,
      error: null,
      successMessage: null,
      phoneError: null,

      changing: false,
      error_password: null,
      password_changed: false,

      // Unsaved changes tracking
      originalProfile: null,
      passwordFields: {
        current: '',
        new: '',
        confirm: ''
      },
      showUnsavedDialog: false,
      pendingRoute: null,
      pendingTab: null,
      currentTab: 0,
      showSubscriptionAlert: false,

      // Billing data
      subscription: null,
      paymentMethod: null,
      invoices: [],
      filteredInvoices: [],
      paginatedInvoices: [],
      loadingInvoices: false,
      changingPlan: false,
      cancelingSubscription: false,
      showPlanOptions: false,

      // Invoice filtering and pagination
      invoiceSearch: '',
      selectedMonth: '',
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 1,

      // Integration data
      googleMapsApiKey: '',
      googleMapsStatus: 'not_connected', // 'not_connected', 'connected', 'invalid'
      googleMapsDialog: false,
      showGoogleMapsKey: false,
      tempGoogleMapsKey: '',
      googleMapsValidationStatus: null,
      loadingGoogleMaps: true, // Loading state for initial fetch

      valueSerpApiKey: '',
      valueSerpStatus: 'not_connected', // 'not_connected', 'connected', 'invalid'
      valueSerpDialog: false,
      showValueSerpKey: false,
      tempValueSerpKey: '',
      valueSerpValidationStatus: null,
      loadingValueSerp: true, // Loading state for initial fetch

      sabSearchEnabled: false,
      savingGoogleMaps: false,
      savingValueSerp: false,
      validatingGoogleMaps: false,
      validatingValueSerp: false,
      disconnectingGoogleMaps: false,
      disconnectingValueSerp: false,

      // Search configuration
      useValueSerp: true, // Default to true, will be updated from backend
      loadingSearchConfig: true, // Loading state for search config

      // DataForSeo data
      dataForSeoUsername: '',
      dataForSeoPassword: '',
      dataForSeoStatus: 'not_connected', // 'not_connected', 'connected', 'invalid'
      dataForSeoDialog: false,
      showDataForSeoPassword: false,
      tempDataForSeoUsername: '',
      tempDataForSeoPassword: '',
      dataForSeoValidationStatus: null,
      savingDataForSeo: false,
      validatingDataForSeo: false,
      disconnectingDataForSeo: false,
      loadingDataForSeo: true, // Loading state for initial fetch

      // DataForSeo Search Toggle
      dataForSeoSearchEnabled: true,
      togglingDataForSeoSearch: false,

      // Disconnect dialog
      showDisconnectDialog: false,
      disconnectServiceName: '',
      disconnectServiceType: '',
      disconnectLoading: false
    }
  },
  computed: {
    ...mapState({
      me: state => state.me.me,
      profile: state => state.me.profile
    }),
    paginationText() {
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredInvoices.length);
      return `${this.filteredInvoices.length} of ${end}`;
    },
    hasUnsavedProfileChanges() {
      if (!this.originalProfile || !this.profile) return false;
      return (
        (this.profile.first_name || '') !== (this.originalProfile.first_name || '') ||
        (this.profile.last_name || '') !== (this.originalProfile.last_name || '') ||
        (this.profile.email || '') !== (this.originalProfile.email || '') ||
        (this.profile.phone || '') !== (this.originalProfile.phone || '')
      );
    },
    hasUnsavedPasswordChanges() {
      return (
        this.passwordFields.current !== '' ||
        this.passwordFields.new !== '' ||
        this.passwordFields.confirm !== ''
      );
    },
    hasUnsavedChanges() {
      return this.hasUnsavedProfileChanges || this.hasUnsavedPasswordChanges;
    }
  },
  methods: {
    resetDataForSeoState() {
      this.dataForSeoUsername = '';
      this.dataForSeoPassword = '';
      this.tempDataForSeoUsername = '';
      this.tempDataForSeoPassword = '';
      this.dataForSeoStatus = 'not_connected';
      this.dataForSeoValidationStatus = null;
      this.dataForSeoSearchEnabled = false;
    },

    fetchDataForSeoFromToken(token) {
      // Helper method to fetch DataForSEO credentials from JWT token
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));

          if (payload.user_metadata &&
              payload.user_metadata.dataForSeoUsername &&
              payload.user_metadata.dataForSeoPassword) {

            this.dataForSeoUsername = payload.user_metadata.dataForSeoUsername;
            this.dataForSeoPassword = payload.user_metadata.dataForSeoPassword;
            this.tempDataForSeoUsername = payload.user_metadata.dataForSeoUsername;
            this.tempDataForSeoPassword = payload.user_metadata.dataForSeoPassword;
            this.dataForSeoStatus = 'connected';
            this.dataForSeoValidationStatus = 'valid';
          } else {
            this.resetDataForSeoState();
          }
        } else {
          this.resetDataForSeoState();
        }
      } catch (decodeError) {
        this.resetDataForSeoState();
      }
    },

    async ensureAuthReady() {
      // First check if auth already has a session
      await this.$store.dispatch('auth/checkAuth');

      // Import Supabase and wait for session to be ready
      const { supabase } = await import('@/config/supabase');

      // Try multiple times to get the session (in case it's still loading)
      let attempts = 0;
      let session = null;

      while (attempts < 5 && !session) {
        const { data } = await supabase.auth.getSession();
        session = data.session;

        if (!session) {
          // Wait a bit before trying again
          await new Promise(resolve => setTimeout(resolve, 200));
          attempts++;
        }
      }

      if (session) {
        // Update store with session if not already there
        if (!this.$store.state.auth.session) {
          this.$store.commit('auth/setSession', session);
          if (session.user) {
            const userData = {
              id: session.user.id,
              email: session.user.email,
              first_name: (session.user.user_metadata && session.user.user_metadata.first_name) || '',
              last_name: (session.user.user_metadata && session.user.user_metadata.last_name) || '',
              phone: (session.user.user_metadata && session.user.user_metadata.phone) || '',
              user_metadata: session.user.user_metadata || {},
              raw_user_meta_data: session.user.raw_user_meta_data || session.user.user_metadata || {},
              // Include DataForSEO directly if present
              dataForSeoUsername: (session.user.user_metadata && session.user.user_metadata.dataForSeoUsername) || '',
              dataForSeoPassword: (session.user.user_metadata && session.user.user_metadata.dataForSeoPassword) || ''
            };
            this.$store.commit('auth/setUser', userData);
          }
        }
      }
    },

    formatPhoneNumber(event) {
      this.phoneError = null;
      const input = event.target.value;

      if (!input) {
        this.profile.phone = '';
        return;
      }

      try {
        // Use AsYouType for real-time formatting
        const formatter = new AsYouType();
        const formatted = formatter.input(input);

        // Validate if it looks like a complete phone number
        if (input.length > 10) {
          if (isValidPhoneNumber(input)) {
            this.phoneError = null;
          } else if (input.startsWith('+')) {
            this.phoneError = 'Please enter a valid phone number with country code';
          }
        }
      } catch (error) {
        // Phone formatting error
      }
    },

    async save() {
      this.saving = true;
      this.error = null;
      this.successMessage = null;
      this.phoneError = null;

      // Validate phone if provided
      if (this.profile.phone && this.profile.phone.trim()) {
        const phone = this.profile.phone.trim();
        // Check if it's a valid format (allow various formats)
        if (phone.length < 10 && !phone.startsWith('+')) {
          this.phoneError = 'Phone number appears to be incomplete';
          this.saving = false;
          return;
        }
      }

      try {
        const response = await this.$store.dispatch('me/save', {
          first_name: this.profile.first_name.trim(),
          last_name: this.profile.last_name.trim(),
          email: this.profile.email.trim(),
          phone: this.profile.phone ? this.profile.phone.trim() : ''
        });

        this.successMessage = 'Profile updated successfully!';
        await this.$store.dispatch('me/fetch');
        // Update original profile after successful save
        this.originalProfile = JSON.parse(JSON.stringify(this.profile));

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
      } catch(error) {
        console.error('Save error:', error);
        this.error = error || 'Failed to update profile';
      }

      this.saving = false;
    },

    async change_password() {
      this.changing = true;
      this.error_password = null;

      // Validate inputs before sending
      const currentPassword = this.$refs.current_password.value;
      const newPassword = this.$refs.new_password.value;
      const confirmPassword = this.$refs.confirm_password.value;

      if (!currentPassword || !newPassword || !confirmPassword) {
        this.error_password = 'All password fields are required';
        this.changing = false;
        return;
      }

      if (newPassword !== confirmPassword) {
        this.error_password = 'New password and confirm password do not match';
        this.changing = false;
        return;
      }

      if (newPassword.length < 6) {
        this.error_password = 'Password must be at least 6 characters long';
        this.changing = false;
        return;
      }

      try {
        await this.$store.dispatch('me/change_password', {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        });

        this.password_changed = true;

        this.$refs.current_password.value = '';
        this.$refs.new_password.value = '';
        this.$refs.confirm_password.value = '';
        // Reset password fields tracking
        this.passwordFields.current = '';
        this.passwordFields.new = '';
        this.passwordFields.confirm = '';

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.password_changed = false;
        }, 3000);
      } catch(error) {
        console.error('Password change error:', error);
        this.error_password = error || 'Failed to change password';
      }

      this.changing = false;
    },

    // Billing methods
    async fetchSubscription() {
      try {
        // Try the new endpoint first
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/current`,
            {
              headers: {
                Authorization: `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
              }
            }
          );

          if (response.data) {
            this.subscription = response.data.subscription;
            this.paymentMethod = response.data.paymentMethod;
            return;
          }
        } catch (currentError) {
          // Try subscription endpoint
        }

        // Fallback to the standard subscription endpoint
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/subscription`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.hasSubscription) {
          this.subscription = response.data.subscription;
          // Payment method might not be included in this endpoint
          // Subscription data from fallback
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    },

    async fetchInvoices() {
      this.loadingInvoices = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoices`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        this.invoices = response.data.invoices || [];
        this.filterInvoices();
      } catch (error) {
        console.error('Error fetching invoices:', error);
        this.invoices = [];
      } finally {
        this.loadingInvoices = false;
      }
    },

    filterInvoices() {
      let filtered = [...this.invoices];

      // Filter by search term
      if (this.invoiceSearch) {
        filtered = filtered.filter(invoice =>
          (invoice.invoice_number || invoice.id || '').toLowerCase().includes(this.invoiceSearch.toLowerCase())
        );
      }

      // Filter by month
      if (this.selectedMonth) {
        filtered = filtered.filter(invoice => {
          const date = new Date(invoice.createdAt);
          const month = String(date.getMonth() + 1).padStart(2, '0');
          return month === this.selectedMonth;
        });
      }

      this.filteredInvoices = filtered;
      this.updatePagination();
    },

    updatePagination() {
      this.totalPages = Math.ceil(this.filteredInvoices.length / this.itemsPerPage);
      this.currentPage = 1;
      this.paginateInvoices();
    },

    paginateInvoices() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.paginatedInvoices = this.filteredInvoices.slice(start, end);
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.paginateInvoices();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.paginateInvoices();
      }
    },

    async changePlanType(planType) {
      // If no subscription, create new one
      if (!this.subscription) {
        const result = await this.$swal({
          title: `Subscribe to ${planType === 'monthly' ? 'Monthly' : 'Yearly'} Plan?`,
          text: 'You will be redirected to our secure payment page.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed'
        });

        if (!result.isConfirmed) return;

        try {
          this.changingPlan = true;
          const token = this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'];

          const response = await axios.post(
            `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/create-checkout-session`,
            { planType },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data.url) {
            window.location.href = response.data.url;
          }
        } catch (error) {
          console.error('Error creating checkout session:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: 'Unable to start checkout process'
          });
        } finally {
          this.changingPlan = false;
        }
        return;
      }

      // Change existing subscription
      const currentPlan = this.subscription.plan_type;
      const newPlan = planType;

      const result = await this.$swal({
        title: 'Change Subscription Plan?',
        html: `You are switching from <b>${currentPlan}</b> to <b>${newPlan}</b> plan.<br><br>
               The change will take effect immediately and you'll be charged/credited prorated amounts.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, change plan'
      });

      if (!result.isConfirmed) return;

      try {
        this.changingPlan = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/update-plan`,
          { newPlanType: planType },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Your subscription plan has been updated!'
          });
          await this.fetchSubscription();
          this.showPlanOptions = false;
        }
      } catch (error) {
        console.error('Error updating plan:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update plan'
        });
      } finally {
        this.changingPlan = false;
      }
    },

    async cancelSubscription() {
      const result = await this.$swal({
        title: 'Cancel Subscription?',
        text: 'Your subscription will remain active until the end of the current billing period.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'Keep subscription'
      });

      if (result.isConfirmed) {
        this.cancelingSubscription = true;
        try {
          const response = await axios.post(
            `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/cancel`,
            {},
            {
              headers: {
                Authorization: `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
              }
            }
          );

          if (response.data.success) {
            this.$swal({
              icon: 'success',
              title: 'Subscription Canceled',
              text: 'Your subscription has been canceled and will end at the period end.'
            });
            await this.fetchSubscription();
          }
        } catch (error) {
          console.error('Error canceling subscription:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: 'Failed to cancel subscription'
          });
        } finally {
          this.cancelingSubscription = false;
        }
      }
    },

    async openCustomerPortal() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/portal`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }
      } catch (error) {
        console.error('Error opening customer portal:', error);

        let errorMessage = 'Failed to open customer portal';
        if (error.response && error.response.data) {
          if (error.response.data.stripeMessage) {
            // Stripe Portal Configuration Required
            errorMessage = 'Payment management portal is being configured. Please try again later or contact support.';
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        this.$swal({
          icon: 'warning',
          title: 'Portal Not Available',
          text: errorMessage,
          footer: 'To update payment method manually, please contact support.'
        });
      }
    },

    async downloadInvoice(invoice) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoice/${invoice.id}/download`,
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }
      } catch (error) {
        console.error('Error downloading invoice:', error);
        this.$swal({
          icon: 'error',
          title: 'Download Failed',
          text: 'Unable to download invoice. Please try again later.'
        });
      }
    },

    viewInvoice(invoice) {
      if (invoice.hosted_invoice_url) {
        window.open(invoice.hosted_invoice_url, '_blank');
      } else {
        // Fallback to download if no hosted URL available
        this.downloadInvoice(invoice);
      }
    },

    formatNextPayment(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    },

    formatInvoiceDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
    },

    getStatusClass(status) {
      if (!status || status === 'paid') return 'status-paid';
      if (status === 'unpaid' || status === 'pending') return 'status-unpaid';
      return 'status-default';
    },

    getCardIcon(brand) {
      const brandIcons = {
        'visa': 'https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg',
        'mastercard': 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg',
        'amex': 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo_%282018%29.svg',
        'discover': 'https://upload.wikimedia.org/wikipedia/commons/5/57/Discover_Card_logo.svg'
      };
      return brandIcons[brand && brand.toLowerCase()] || brandIcons['visa'];
    },

    formatCardBrand(brand) {
      if (!brand) return 'Card';
      const formatted = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
      return formatted === 'Amex' ? 'American Express' : formatted;
    },

    getPlanDisplayName() {
      if (!this.subscription) return 'No active subscription';
      if (this.subscription.plan_type === 'monthly') return 'Monthly Plan';
      if (this.subscription.plan_type === 'yearly') return 'Yearly Plan';
      // Fallback for old plan names or custom plans
      return this.subscription.plan_name || this.subscription.plan_type || 'Custom Plan';
    },

    getPlanPrice() {
      if (!this.subscription) return '';
      if (this.subscription.plan_type === 'monthly') return '$10/month';
      if (this.subscription.plan_type === 'yearly') return '$120/year';
      // Fallback for old plan pricing
      if (this.subscription.price) return this.subscription.price;
      if (this.subscription.amount) return `$${this.subscription.amount}`;
      return '';
    },

    getInvoiceDescription(invoice) {
      // Check for plan description in invoice
      if (invoice.description) return invoice.description;
      if (invoice.plan_name) return invoice.plan_name;
      if (invoice.plan_type) {
        if (invoice.plan_type === 'monthly') return 'Monthly Plan';
        if (invoice.plan_type === 'yearly') return 'Yearly Plan';
        return invoice.plan_type;
      }
      // Fallback to subscription plan if available
      if (this.subscription) {
        return this.getPlanDisplayName();
      }
      return 'Subscription';
    },

    formatInvoiceAmount(invoice) {
      // Check various possible amount fields
      if (invoice.amount_paid) {
        return `$${parseFloat(invoice.amount_paid).toFixed(2)}`;
      }
      if (invoice.amount) {
        return `$${parseFloat(invoice.amount).toFixed(2)}`;
      }
      if (invoice.total) {
        return `$${parseFloat(invoice.total).toFixed(2)}`;
      }
      // Fallback based on plan type
      if (invoice.plan_type === 'monthly') return '$10.00';
      if (invoice.plan_type === 'yearly') return '$120.00';
      return '-';
    },

    formatInvoiceStatus(status) {
      if (!status) return 'Unknown';
      // Capitalize first letter
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    getInvoiceStatusClass(status) {
      if (!status) return 'status-unpaid';
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === 'paid' || lowerStatus === 'succeeded') {
        return 'status-paid';
      }
      if (lowerStatus === 'pending' || lowerStatus === 'processing') {
        return 'status-pending';
      }
      return 'status-unpaid';
    },

    // Integration methods
    async fetchSearchConfig() {
      this.loadingSearchConfig = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/config`,
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']
            }
          }
        );

        if (response.data.success) {
          this.useValueSerp = response.data.useValueSerp;
          this.dataForSeoSearchEnabled = response.data.dataForSeoSearchEnabled !== false; // Default to true if not set
        }
      } catch (error) {
        console.error('Failed to fetch search config:', error);
        // Continue with defaults if config fetch fails
      } finally {
        this.loadingSearchConfig = false;
      }
    },

    async fetchApiKeys() {
      // Set loading states for all integrations
      this.loadingGoogleMaps = true;
      this.loadingValueSerp = true;
      this.loadingDataForSeo = true;

      try {
        // Clear the store cache first to ensure fresh data (await it to ensure it completes)
        await this.$store.dispatch('apiKeys/clearApiKeys');

        // Force refresh ALL API keys from the store to ensure we get the correct user's data
        await Promise.all([
          this.$store.dispatch('apiKeys/fetchGoogleMapsKey'),
          this.$store.dispatch('apiKeys/fetchValueSerpKey'),
          this.$store.dispatch('apiKeys/fetchDataForSeoKey')
        ]);

        // Use the store's state
        const googleMapsKey = this.$store.getters['apiKeys/googleMapsKey'];
        const googleMapsStatus = this.$store.getters['apiKeys/googleMapsStatus'];
        const isGoogleMapsConfigured = this.$store.getters['apiKeys/isGoogleMapsConfigured'];

        if (isGoogleMapsConfigured) {
          this.googleMapsApiKey = googleMapsKey || '';
          this.tempGoogleMapsKey = googleMapsKey || '';
          this.googleMapsStatus = googleMapsStatus === 1 ? 'connected' : 'invalid';
          this.googleMapsValidationStatus = googleMapsStatus === 1 ? 'valid' : 'invalid';
        } else {
          // Reset to not connected state
          this.googleMapsApiKey = '';
          this.tempGoogleMapsKey = '';
          this.googleMapsStatus = 'not_connected';
          this.googleMapsValidationStatus = null;
        }
      } catch (error) {
        console.error('Error fetching Google Maps key:', error);
        // Reset on error
        this.googleMapsApiKey = '';
        this.tempGoogleMapsKey = '';
        this.googleMapsStatus = 'not_connected';
        this.googleMapsValidationStatus = null;
      } finally {
        this.loadingGoogleMaps = false;
      }

      // Get ValueSerp data from store
      const valueSerpKey = this.$store.getters['apiKeys/valueSerpKey'];
      const valueSerpStatus = this.$store.getters['apiKeys/valueSerpStatus'];
      const isValueSerpConfigured = this.$store.getters['apiKeys/isValueSerpConfigured'];

      if (isValueSerpConfigured) {
        this.valueSerpApiKey = valueSerpKey || '';
        this.tempValueSerpKey = valueSerpKey || '';
        this.valueSerpStatus = valueSerpStatus === 1 ? 'connected' : 'invalid';
        this.valueSerpValidationStatus = valueSerpStatus === 1 ? 'valid' : 'invalid';
        // Enable SAB Search toggle if ValueSerp status is 1 (valid)
        this.sabSearchEnabled = valueSerpStatus === 1;
      } else {
        // Reset to not connected state
        this.valueSerpApiKey = '';
        this.tempValueSerpKey = '';
        this.valueSerpStatus = 'not_connected';
        this.valueSerpValidationStatus = null;
        this.sabSearchEnabled = false;
      }

      // Reset ValueSerp loading state
      this.loadingValueSerp = false;

      // Get DataForSeo data - Fetch from backend API
      try {
        const token = this.$store.getters['auth/jwt'] ||
                     this.$store.getters['auth/accessToken'] ||
                     localStorage.getItem('auth_token');

        if (token) {
          // First, try to fetch from backend API (which has the latest data)
          try {
            const response = await axios.get(
              `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/dataforseo`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );

            if (response.data.success && response.data.data) {
              const data = response.data.data;
              if (data.is_configured && data.username) {
                // Credentials exist in backend
                this.dataForSeoUsername = data.username;
                // Password is not returned for security, keep existing if we have it
                if (!this.dataForSeoPassword) {
                  // Try to get password from JWT token if we don't have it locally
                  try {
                    const parts = token.split('.');
                    if (parts.length === 3) {
                      const payload = JSON.parse(atob(parts[1]));
                      if (payload.user_metadata && payload.user_metadata.dataForSeoPassword) {
                        this.dataForSeoPassword = payload.user_metadata.dataForSeoPassword;
                      }
                    }
                  } catch (e) {
                    // Password remains empty if not in token
                  }
                }
                this.tempDataForSeoUsername = this.dataForSeoUsername;
                this.tempDataForSeoPassword = this.dataForSeoPassword;
                this.dataForSeoStatus = data.status === 1 ? 'connected' : 'invalid';
                this.dataForSeoValidationStatus = data.status === 1 ? 'valid' : 'invalid';
              } else {
                // No credentials configured in backend
                this.resetDataForSeoState();
              }
            } else {
              // No data from backend, fall back to token metadata
              this.fetchDataForSeoFromToken(token);
            }
          } catch (apiError) {
            // API call failed, fall back to token metadata
            console.error('Failed to fetch DataForSeo from API:', apiError);
            this.fetchDataForSeoFromToken(token);
          }

          // Always fetch search config
          try {
            const configResponse = await axios.get(
              `${process.env.VUE_APP_API_ENDPOINT}/api/scans/config`,
              {
                headers: {
                  'Authorization': 'Bearer ' + token
                }
              }
            );
            if (configResponse.data.success) {
              this.dataForSeoSearchEnabled = configResponse.data.dataForSeoSearchEnabled !== false;
            }
          } catch (error) {
            console.error('Failed to fetch search config:', error);
            this.dataForSeoSearchEnabled = false;
          }
        } else {
          this.resetDataForSeoState();
        }
      } catch (error) {
        console.error('Error in DataForSeo fetch:', error);
        this.resetDataForSeoState();
      } finally {
        // Reset DataForSeo loading state
        this.loadingDataForSeo = false;
      }
    },

    openGoogleMapsDialog() {
      this.googleMapsDialog = true;
      this.tempGoogleMapsKey = this.googleMapsApiKey;
    },

    openValueSerpDialog() {
      this.valueSerpDialog = true;
      this.tempValueSerpKey = this.valueSerpApiKey;
    },

    async updateGoogleMapsKey() {
      if (!this.tempGoogleMapsKey) return;

      this.savingGoogleMaps = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps`,
          {
            api_key: this.tempGoogleMapsKey.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.success) {
          this.googleMapsApiKey = this.tempGoogleMapsKey;
          this.googleMapsStatus = response.data.status === 1 ? 'connected' : 'invalid';
          this.googleMapsValidationStatus = response.data.status === 1 ? 'valid' : 'invalid';
          this.googleMapsDialog = false;

          // Update the Vuex store with the new API key data
          this.$store.commit('apiKeys/SET_GOOGLE_MAPS_KEY', response.data.data);

          // Also refresh the API key from backend to ensure consistency
          await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');

          this.$swal({
            title: 'API Key Validated!',
            html: `
              <div style="text-align: left; padding: 10px;">
                <!-- Success Items -->
                <div style="margin-bottom: 20px;">
                  <div style="display: flex; align-items: center; margin-bottom: 12px;">
                    <div style="width: 32px; height: 32px; background: #10B981; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0;">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <span style="color: #19233A; font-size: 15px; font-weight: 500;">Places API - Working</span>
                  </div>
                  <div style="display: flex; align-items: center;">
                    <div style="width: 32px; height: 32px; background: #10B981; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0;">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <span style="color: #19233A; font-size: 15px; font-weight: 500;">Geocoding API - Working</span>
                  </div>
                </div>

                <!-- Important Notice -->
                <div style="background: linear-gradient(135deg, #2477F6 0%, #1e63d4 100%); border-radius: 8px; padding: 16px; margin-bottom: 16px;">
                  <div style="display: flex; align-items: start;">
                    <div style="margin-right: 12px; flex-shrink: 0;">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="white"/>
                      </svg>
                    </div>
                    <div>
                      <div style="color: white; font-size: 15px; font-weight: 600; margin-bottom: 6px;">One More Step Required</div>
                      <div style="color: rgba(255,255,255,0.95); font-size: 14px; line-height: 1.5;">
                        Please ensure <strong>Maps JavaScript API</strong> is also enabled in your Google Cloud Console.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Explanation -->
                <div style="background: #f6f6f7; border-radius: 6px; padding: 14px;">
                  <div style="color: #19233A; font-size: 13px; font-weight: 600; margin-bottom: 6px;">Why is this needed?</div>
                  <div style="color: #6B7280; font-size: 13px; line-height: 1.5;">
                    Maps JavaScript API controls the interactive maps throughout the application. Without it, maps won't display when you create or view scans.
                  </div>
                </div>
              </div>
            `,
            width: '650px',
            showConfirmButton: true,
            confirmButtonText: 'Got it!',
            confirmButtonColor: '#2477F6',
            customClass: {
              popup: 'custom-swal-popup',
              title: 'custom-swal-title',
              htmlContainer: 'custom-swal-html'
            }
          });
        }
      } catch (error) {
        console.error('Error updating Google Maps key:', error);

        // Get detailed error message from backend
        const errorMessage = (error.response && error.response.data && error.response.data.message)
          || 'Failed to update Google Maps API key';

        this.$swal({
          icon: 'error',
          title: 'Validation Failed',
          html: `<div style="text-align: left;">
            <p><strong>${errorMessage}</strong></p>
            <br>
            <p><strong>Required APIs to enable in Google Cloud Console:</strong></p>
            <ol style="margin-left: 20px; line-height: 1.8;">
              <li>Maps JavaScript API</li>
              <li>Places API</li>
              <li>Geocoding API</li>
            </ol>
            <br>
            <p style="font-size: 0.9em; color: #666;">
              Go to <a href="https://console.cloud.google.com/apis/library" target="_blank" style="color: #2477f6;">Google Cloud Console → APIs & Services → Library</a>
              and enable all three APIs for your API key.
            </p>
          </div>`,
          width: '600px',
          confirmButtonText: 'OK'
        });
      } finally {
        this.savingGoogleMaps = false;
      }
    },

    // Revalidate method commented out as requested
    /* async revalidateGoogleMaps() {
      if (!this.tempGoogleMapsKey) return;

      this.validatingGoogleMaps = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps/validate`,
          {
            api_key: this.tempGoogleMapsKey.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        // Check if validation was successful based on the actual response structure
        if (response.data.success && response.data.data && response.data.data.status === 1) {
          this.googleMapsValidationStatus = 'valid';
          this.$swal({
            icon: 'success',
            title: 'Valid',
            text: response.data.message || 'Google Maps API key is valid and working!'
          });
        } else {
          this.googleMapsValidationStatus = 'invalid';
          this.$swal({
            icon: 'error',
            title: 'Invalid',
            text: response.data.message || 'Google Maps API key validation failed'
          });
        }
      } catch (error) {
        console.error('Error validating Google Maps key:', error);
        this.googleMapsValidationStatus = 'invalid';
      } finally {
        this.validatingGoogleMaps = false;
      }
    }, */

    async disconnectGoogleMaps() {
      this.disconnectServiceName = 'Google Maps';
      this.disconnectServiceType = 'google_maps';
      this.showDisconnectDialog = true;
    },

    cancelDisconnect() {
      this.showDisconnectDialog = false;
      this.disconnectServiceName = '';
      this.disconnectServiceType = '';
    },

    async confirmDisconnect() {
      this.disconnectLoading = true;

      try {
        let endpoint = '';
        let clearState = null;

        switch(this.disconnectServiceType) {
          case 'google_maps':
            endpoint = 'google-maps';
            this.disconnectingGoogleMaps = true;
            clearState = () => {
              this.googleMapsApiKey = '';
              this.tempGoogleMapsKey = '';
              this.googleMapsStatus = 'not_connected';
              this.googleMapsValidationStatus = null;
              this.googleMapsDialog = false;
              this.disconnectingGoogleMaps = false;

              // Clear the Vuex store state as well
              this.$store.commit('apiKeys/SET_GOOGLE_MAPS_KEY', null);
            };
            break;
          case 'value_serp':
            endpoint = 'value-serp';
            this.disconnectingValueSerp = true;
            clearState = () => {
              this.valueSerpApiKey = '';
              this.tempValueSerpKey = '';
              this.valueSerpStatus = 'not_connected';
              this.valueSerpValidationStatus = null;
              this.valueSerpDialog = false;
              this.sabSearchEnabled = false;
              this.disconnectingValueSerp = false;
            };
            break;
          case 'dataforseo':
            endpoint = 'dataforseo';
            this.disconnectingDataForSeo = true;
            clearState = () => {
              this.dataForSeoUsername = '';
              this.dataForSeoPassword = '';
              this.tempDataForSeoUsername = '';
              this.tempDataForSeoPassword = '';
              this.dataForSeoStatus = 'not_connected';
              this.dataForSeoValidationStatus = null;
              this.dataForSeoDialog = false;
              this.dataForSeoSearchEnabled = false; // Reset the toggle
              this.disconnectingDataForSeo = false;
            };
            break;
        }

        const response = await axios.delete(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/${endpoint}`,
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        // Only clear state if the deletion was successful
        if (response.data && response.data.success) {
          clearState();
          this.showDisconnectDialog = false;

          this.$swal({
            icon: 'success',
            title: 'Disconnected',
            text: `${this.disconnectServiceName} has been disconnected successfully`
          });
        } else {
          throw new Error((response.data && response.data.message) || 'Failed to disconnect');
        }
      } catch (error) {
        console.error(`Error disconnecting ${this.disconnectServiceName}:`, error);

        // Check if it's a specific error message
        const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || `Failed to disconnect ${this.disconnectServiceName}`;

        this.$swal({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        });

        // Don't clear state on error
        this.showDisconnectDialog = false;
      } finally {
        this.disconnectLoading = false;
        this.disconnectServiceName = '';
        this.disconnectServiceType = '';

        // Reset the disconnecting flags
        this.disconnectingGoogleMaps = false;
        this.disconnectingValueSerp = false;
        this.disconnectingDataForSeo = false;
      }
    },

    async updateValueSerpKey() {
      if (!this.tempValueSerpKey) return;

      this.savingValueSerp = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/value-serp`,
          {
            api_key: this.tempValueSerpKey.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.success) {
          this.valueSerpApiKey = this.tempValueSerpKey;
          this.valueSerpStatus = response.data.status === 1 ? 'connected' : 'invalid';
          this.valueSerpValidationStatus = response.data.status === 1 ? 'valid' : 'invalid';

          // Enable SAB Search if ValueSerp is valid
          this.sabSearchEnabled = response.data.status === 1;

          this.valueSerpDialog = false;

          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Value SERP API key updated successfully!'
          });
        }
      } catch (error) {
        console.error('Error updating Value SERP key:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update Value SERP API key'
        });
      } finally {
        this.savingValueSerp = false;
      }
    },

    async revalidateValueSerp() {
      if (!this.tempValueSerpKey) return;

      this.validatingValueSerp = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/value-serp/validate`,
          {
            api_key: this.tempValueSerpKey.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        // Check if validation was successful based on the actual response structure
        if (response.data.success && response.data.data && response.data.data.status === 1) {
          this.valueSerpValidationStatus = 'valid';
          this.$swal({
            icon: 'success',
            title: 'Valid',
            text: response.data.message || 'Value SERP API key is valid and working!'
          });
        } else {
          this.valueSerpValidationStatus = 'invalid';
          this.$swal({
            icon: 'error',
            title: 'Invalid',
            text: response.data.message || 'Value SERP API key validation failed'
          });
        }
      } catch (error) {
        console.error('Error validating Value SERP key:', error);
        this.valueSerpValidationStatus = 'invalid';
      } finally {
        this.validatingValueSerp = false;
      }
    },

    async disconnectValueSerp() {
      this.disconnectServiceName = 'Value SERP';
      this.disconnectServiceType = 'value_serp';
      this.showDisconnectDialog = true;
    },

    async updateSabSearch() {
      // Show alert if disabling SAB Search
      if (!this.sabSearchEnabled) {
        this.$swal({
          icon: 'info',
          title: 'SAB Search Disabled',
          text: 'SAB Search has been disabled. You can enable it again at any time.'
        });
      }

      try {
        // Update the ValueSerp status based on toggle (1 for enabled, 0 for disabled)
        const newStatus = this.sabSearchEnabled ? 1 : 0;

        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/value-serp/sab-search`,
          {
            status: newStatus
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.success) {
          // Update the ValueSerp status based on the toggle
          if (this.valueSerpApiKey) {
            this.valueSerpStatus = newStatus === 1 ? 'connected' : 'invalid';
            this.valueSerpValidationStatus = newStatus === 1 ? 'valid' : 'invalid';
          }
        }
      } catch (error) {
        console.error('Error updating SAB search setting:', error);
        // Revert the change if it fails
        this.sabSearchEnabled = !this.sabSearchEnabled;

        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update SAB Search setting. Please try again.'
        });
      }
    },

    // DataForSeo methods
    openDataForSeoDialog() {
      this.dataForSeoDialog = true;
      this.tempDataForSeoUsername = this.dataForSeoUsername || '';
      this.tempDataForSeoPassword = this.dataForSeoPassword || '';
    },

    async updateDataForSeoKey() {
      if (!this.tempDataForSeoUsername || !this.tempDataForSeoPassword) return;

      this.savingDataForSeo = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/dataforseo`,
          {
            username: this.tempDataForSeoUsername.trim(),
            password: this.tempDataForSeoPassword.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        if (response.data.success) {
          this.dataForSeoUsername = this.tempDataForSeoUsername;
          this.dataForSeoPassword = this.tempDataForSeoPassword;
          this.dataForSeoStatus = response.data.status === 1 ? 'connected' : 'invalid';
          this.dataForSeoValidationStatus = response.data.status === 1 ? 'valid' : 'invalid';
          this.dataForSeoDialog = false;

          this.$swal({
            icon: 'success',
            title: 'Success',
            text: response.data.message || 'DataForSeo API key updated successfully!'
          });
        }
      } catch (error) {
        console.error('Error updating DataForSeo key:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update DataForSeo API key'
        });
      } finally {
        this.savingDataForSeo = false;
      }
    },

    async revalidateDataForSeo() {
      // Validation commented out as requested
      return;
      /*
      if (!this.tempDataForSeoUsername || !this.tempDataForSeoPassword) return;

      this.validatingDataForSeo = true;
      try {
        // Create Base64 encoded key for validation
        const apiKey = btoa(`${this.tempDataForSeoUsername.trim()}:${this.tempDataForSeoPassword.trim()}`);
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/dataforseo/validate`,
          {
            api_key: apiKey
          },
          {
            headers: {
              'Authorization': `Bearer ${this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']}`
            }
          }
        );

        // Check if validation was successful based on the actual response structure
        if (response.data.success && response.data.data && response.data.data.status === 1) {
          this.dataForSeoValidationStatus = 'valid';
          this.$swal({
            icon: 'success',
            title: 'Valid',
            text: response.data.message || 'DataForSeo credentials are valid and working!'
          });
        } else {
          this.dataForSeoValidationStatus = 'invalid';
          this.$swal({
            icon: 'error',
            title: 'Invalid',
            text: response.data.message || 'DataForSeo credentials validation failed'
          });
        }
      } catch (error) {
        console.error('Error validating DataForSeo credentials:', error);
        this.dataForSeoValidationStatus = 'invalid';
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to validate DataForSeo credentials'
        });
      } finally {
        this.validatingDataForSeo = false;
      }
      */
    },

    async disconnectDataForSeo() {
      this.disconnectServiceName = 'DataForSeo';
      this.disconnectServiceType = 'dataforseo';
      this.showDisconnectDialog = true;
    },

    // Toggle function commented out as requested
    async toggleDataForSeoSearch() {
      // Functionality disabled
      return;
      /*
      this.togglingDataForSeoSearch = true;

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/toggle-dataforseo-search`,
          {
            enabled: this.dataForSeoSearchEnabled
          },
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']
            }
          }
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: response.data.message || 'DataForSEO search setting updated',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error toggling DataForSEO search:', error);
        // Revert the toggle on error
        this.dataForSeoSearchEnabled = !this.dataForSeoSearchEnabled;

        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update DataForSEO search setting'
        });
      } finally {
        this.togglingDataForSeoSearch = false;
      }
      */
    },

    // Unsaved changes methods
    onTabChange(newTab) {
      // Check if there are unsaved changes when switching tabs
      if (this.hasUnsavedChanges) {
        // Store the target tab
        const targetTab = newTab;
        // Revert to current tab
        this.$nextTick(() => {
          this.currentTab = this.currentTab === 0 ? 0 : this.currentTab;
        });
        // Show dialog
        this.showUnsavedDialog = true;
        this.pendingTab = targetTab;
      } else if (newTab === 3) {
        // If switching to integrations tab
        // fetchApiKeys removed - now only runs on mount
      }
    },

    cancelNavigation() {
      this.showUnsavedDialog = false;
      this.pendingRoute = null;
      this.pendingTab = null;
    },

    async saveAndNavigate() {
      this.showUnsavedDialog = false;

      // Save based on current tab
      if (this.currentTab === 0 && this.hasUnsavedProfileChanges) {
        await this.save();
      } else if (this.currentTab === 1 && this.hasUnsavedPasswordChanges) {
        await this.change_password();
      }

      // Navigate after save
      if (this.pendingRoute) {
        this.$router.push(this.pendingRoute);
        this.pendingRoute = null;
      } else if (this.pendingTab !== null) {
        this.currentTab = this.pendingTab;
        this.pendingTab = null;
      }
    },

    // Track password field changes
    trackPasswordChanges() {
      if (this.$refs.current_password) {
        this.passwordFields.current = this.$refs.current_password.value || '';
      }
      if (this.$refs.new_password) {
        this.passwordFields.new = this.$refs.new_password.value || '';
      }
      if (this.$refs.confirm_password) {
        this.passwordFields.confirm = this.$refs.confirm_password.value || '';
      }
    }
  },
  watch: {
    // Watch for user changes - fetchApiKeys removed, now only runs on mount
    '$store.state.auth.user': {
      handler(newUser, oldUser) {
        // User change detection kept for potential future use
        if (newUser && oldUser && newUser.id !== oldUser.id) {
          // User changed - no automatic refresh
        }
      },
      deep: true
    },
    profile: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.originalProfile) {
          this.originalProfile = JSON.parse(JSON.stringify(newVal));
        }
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.pendingRoute = to;
      this.showUnsavedDialog = true;
    } else {
      next();
    }
  },
  beforeDestroy() {
    // Clean up event listeners - removed aggressive refresh listeners
  },
  created() {
    this.$store.dispatch('me/fetch');
    this.fetchSubscription();
    this.fetchInvoices();
  },
  async mounted() {
    // Ensure auth is ready before fetching API keys
    await this.ensureAuthReady();

    // REMOVED: Aggressive window focus and visibility change listeners that were
    // causing excessive API calls and reverting user updates.
    // Now API keys are only fetched when:
    // 1. Component mounts (once)
    // 2. User switches to integrations tab
    // 3. User manually refreshes (if we add a button)

    // Check if redirected due to subscription requirement
    if (this.$route.query.subscriptionRequired === 'true') {
      this.currentTab = 2; // Switch to Billing tab (index 2)
      this.showSubscriptionAlert = true;
      // Remove the query parameter from URL without navigation
      const newQuery = { ...this.$route.query };
      delete newQuery.subscriptionRequired;
      delete newQuery.tab;
      this.$router.replace({ query: newQuery });
    } else if (this.$route.query.tab === 'billing') {
      this.currentTab = 2; // Switch to Billing tab
    }

    // Fetch search configuration first
    await this.fetchSearchConfig();

    // Always fetch API keys on mount regardless of current tab
    // This ensures API keys are loaded once when the component is created
    this.fetchApiKeys();
  }
}
</script>

<style scoped>
.setting-header {
  background-color: #fbfbfb;
}
.setting-body {
  background-color: #fdfdfd;
}
.gravatar-pic {
  border-radius: 50%;
  width: 100px;
  display: initial;
  margin-bottom: 6px;
}

/* Billing Tab Styles - Matching billing.png */
.billing-page {
  padding: 20px 0;
}

.top-cards-row {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.plan-card {
  flex: 6; /* 60% width */
  background: #f6f6f7;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.payment-card {
  flex: 4; /* 40% width */
  background: #f6f6f7;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  min-height: 24px; /* Ensure consistent height */
}

.header-left {
  display: flex;
  align-items: baseline; /* Use baseline alignment for text */
  gap: 10px;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #19233A;
  line-height: 1.2;
}

.upgrade-link {
  color: #3b82f6;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 8px;
  line-height: 1.2;
}

.upgrade-link:hover {
  color: #2563eb;
}

.cancel-link {
  color: #2477F6;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  background: transparent;
  border: 1px solid #2477F6;
  padding: 12px;
  border-radius: 5px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  height: fit-content;
}

.cancel-link:hover {
  background: rgba(36, 119, 246, 0.1);
  text-decoration: none;
}

.cancel-link[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.edit-link {
  color: #3b82f6;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 8px;
  line-height: 1.2;
}

.edit-link:hover {
  color: #2563eb;
}

.plan-body {
  margin-bottom: 20px;
}

.plan-name {
  font-size: 14px;
  color: #19233A;
  margin-bottom: 10px;
  font-weight: 500;
}

.plan-status-row {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}

.yes-label {
  color: #10b981;
  font-weight: 600;
}

.plan-price {
  font-size: 14px;
  color: #19233A;
  margin-bottom: 10px;
  font-weight: 500;
}

.next-payment-row {
  font-size: 13px;
  color: #6b7280; /* Light color for "Next Payment :" text */
  display: flex;
  justify-content: end;
}


.payment-date {
  color: #19233A; /* Dark color for the actual date */
  font-weight: 600;
  padding: 1px;
}

/* Old cancel button styles removed - now using cancel-link */

/* Plan Options */
.plan-options {
  border-top: 1px solid #e5e7eb;
  margin-top: 20px;
  padding-top: 20px;
}

.plan-option {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s;
}

.plan-option:hover {
  border-color: #d1d5db;
}

.plan-option.active-plan {
  border-color: #10b981;
  background: #f0fdf4;
}

.plan-option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.plan-option-header h4 {
  font-size: 14px;
  font-weight: 600;
  color: #19233A;
  margin: 0 0 4px 0;
}

.plan-option-price {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.switch-plan-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.switch-plan-btn:hover {
  background: #2563eb;
}

.switch-plan-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.current-plan-badge {
  background: #10b981;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Payment Method Styles */
.payment-body {
  margin-top: 8px;
}

.card-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.card-logo {
  width: 40px;
  height: 25px;
  object-fit: contain;
}

.card-number {
  font-size: 14px;
  color: #19233A;
  font-weight: 500;
}

.card-expiry {
  font-size: 13px;
  color: #6b7280;
}

.no-payment {
  font-size: 14px;
  color: #9ca3af;
  font-style: italic;
}

/* Billing History Section */
.billing-history-card {
  background: white;
  /* border: 1px solid #e5e7eb; */
  /* border-radius: 8px; */
  /* padding: 20px; */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.history-title {
  font-size: 15px;
  font-weight: 600;
  color: #19233A;
  margin-bottom: 20px;
  margin-top: 0;
}

.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 300px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 13px;
}

.search-box input {
  width: 100%;
  padding: 7px 12px 7px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}

.search-box input::placeholder {
  color: #9ca3af;
}

.search-box input:focus {
  border-color: #3b82f6;
}

.month-filter {
  padding: 7px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  color: #19233A;
}

.month-filter:focus {
  border-color: #3b82f6;
}

/* History Table */
.history-table-wrapper {
  width: 100%;
  overflow-x: visible;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.history-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.history-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #19233A;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.history-table th i {
  margin-left: 4px;
  font-size: 10px;
  color: #9ca3af;
  cursor: pointer;
}

.history-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
}

.history-table tbody tr:hover {
  background: #f9fafb;
}

.history-table td {
  padding: 12px;
  font-size: 13px;
  color: #19233A;
}

.history-table .text-center {
  text-align: center;
  color: #9ca3af;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.status-paid {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.status-unpaid {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.view-btn {
  background: transparent;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.view-btn:hover {
  color: #2563eb;
}

.view-btn i {
  font-size: 14px;
}

.download-btn {
  background: transparent;
  border: none;
  color: #10b981;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.download-btn:hover {
  color: #059669;
}

.download-btn i {
  font-size: 14px;
}

/* Pagination */
.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
}

.show-entries {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  outline: none;
}

.entries-select:focus {
  border-color: #3b82f6;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
}

.page-controls {
  display: flex;
  gap: 6px;
}

.page-btn {
  padding: 5px 8px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn i {
  font-size: 11px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  /* Profile Tab Mobile Styles */
  .gravatar-pic {
    width: 80px !important;
    height: 80px !important;
    margin: 0 auto;
  }

  /* Remove padding on mobile for form fields */
  .w-full.sm\:w-1\/2.pr-6 {
    padding-right: 0 !important;
  }

  /* Change Password Tab Mobile */
  .w-full.sm\:w-1\/3.pr-6 {
    padding-right: 0 !important;
  }

  /* Billing Tab Mobile Styles */
  .billing-page {
    padding: 10px;
  }

  .top-cards-row {
    flex-direction: column;
    gap: 16px;
  }

  .plan-card,
  .usage-card {
    width: 100%;
    max-width: 100%;
  }

  .plan-options {
    flex-direction: column;
    gap: 12px;
  }

  .plan-option {
    width: 100%;
    padding: 15px;
  }

  .plan-features {
    margin-left: 0;
  }

  .payment-cards-grid {
    grid-template-columns: 1fr;
  }

  .controls-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-box {
    max-width: 100%;
    margin-bottom: 10px;
  }

  .export-btn {
    width: 100%;
    justify-content: center;
  }

  /* Billing History Table */
  .history-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .history-table {
    font-size: 12px;
    min-width: 500px;
  }

  .history-table th,
  .history-table td {
    padding: 8px 6px;
    font-size: 11px;
  }

  .history-table .status-badge {
    padding: 3px 8px;
    font-size: 10px;
  }

  .history-table .download-link {
    font-size: 11px;
  }

  .pagination-row {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }

  .page-info {
    order: 2;
    text-align: center;
  }

  .pagination-buttons {
    order: 1;
  }

  /* Integrations Tab Mobile */
  .integrations-page {
    padding: 10px 0;
  }

  .integration-card {
    padding: 16px;
  }

  .integration-header {
    flex-direction: column;
    gap: 16px;
  }

  .integration-actions {
    width: 100%;
    flex-direction: row;
    gap: 8px;
  }

  .connect-btn,
  .tutorial-btn,
  .disconnect-btn,
  .update-btn {
    flex: 1;
    padding: 8px 12px;
    font-size: 12px;
  }

  .integration-description {
    margin-left: 0;
    margin-top: 16px;
  }

  .description-content {
    flex-direction: column;
  }

  .sab-search-section,
  .search-toggle-section {
    margin-top: 12px;
    padding-top: 12px;
  }

  .toggle-hint {
    margin-left: 0;
    margin-top: 8px;
  }

  /* Tab Navigation Mobile */
  .v-tabs {
    margin-left: -16px;
    margin-right: -16px;
  }

  .v-tab {
    font-size: 13px;
    min-width: auto;
    padding: 0 12px;
  }

  /* General Form Elements Mobile */
  .rg-text-field {
    font-size: 14px;
  }

  /* Subscription Alert Mobile */
  .subscription-alert {
    padding: 16px;
    margin-bottom: 16px;
  }

  .subscription-alert h2 {
    font-size: 16px;
  }

  .subscription-alert p {
    font-size: 13px;
  }

  .view-plans-btn {
    width: 100%;
  }

  /* Plan upgrade modal mobile */
  .v-dialog {
    margin: 16px !important;
  }

  /* Dashboard box padding adjustment */
  .dashboard-box {
    margin-left: -10px;
    margin-right: -10px;
  }

  .dashboard-box .v-card {
    border-radius: 0 !important;
  }

  /* Buttons alignment mobile */
  .float-right {
    float: none !important;
    display: block;
    width: 100%;
    margin-top: 20px;
  }

  .float-right > div {
    width: 100%;
    text-align: center;
  }

  /* Success/Error messages mobile */
  .bg-green-100,
  .bg-red-100 {
    margin: 16px 0;
    font-size: 13px;
  }
}

/* Integrations Tab Styles */
.integrations-page {
  padding: 20px 0;
}

.connected-apps-title {
  font-size: 16px;
  font-weight: 600;
  color: #19233A;
  margin-bottom: 24px;
}

.integration-card {
  background: #f6f6f7;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
}

.integration-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.integration-title-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}



.integration-title {
  font-size: 14px;
  font-weight: bold;
  color: #19233A;
  margin: 0 0 8px 0;
}

.status-chip {
  margin-top: 4px !important;
}

.integration-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  width: 180px;
  flex-shrink: 0;
}

.connect-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.connect-btn:hover {
  background: #2563eb;
}

.tutorial-btn {
  background: transparent;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  padding: 8px 20px;
  border-radius: 6px;
  white-space: nowrap;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: 100%;
}

.tutorial-btn:hover {
  background: #3b82f6;
  color: white;
}

.integration-description {
  margin-left: 52px;
}

.description-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.description-text {
  flex: 1;
}

.integration-description p {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.5;
}

.integration-features {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
}

.integration-features li {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 6px;
}

.sab-search-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.sab-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #19233A;
  font-weight: 500;
}

.sab-toggle input[type="checkbox"] {
  position: relative;
  width: 44px;
  height: 24px;
  appearance: none;
  background: #cbd5e1;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.sab-toggle input[type="checkbox"]:checked {
  background: #3b82f6;
}

.sab-toggle input[type="checkbox"]::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.sab-toggle input[type="checkbox"]:checked::after {
  transform: translateX(20px);
}

.sab-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin-left: 56px;
}

/* Dialog Styles */
.integration-dialog {
  border: none !important;
  overflow: hidden;
}

.dialog-header {
  background: #19233a;
  color: white;
  padding: 10px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 !important;
  border: none !important;
}

.dialog-header span {
  font-size: 16px;
  font-weight: 500;
}

.dialog-content {
  padding: 20px 0;
}

.api-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.api-input-wrapper {
  position: relative;
  margin-bottom: 16px;
}

.api-input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #19233A;
  background: white;
  transition: border-color 0.2s;
}

.api-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.eye-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.eye-btn:hover {
  color: #374151;
}

.validation-chip {
  margin-top: 12px !important;
}

.dialog-actions {
  padding: 16px 20px !important;
  background: #f9fafb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.disconnect-btn {
  background: transparent;
  color: #2477F6;
  border: 1px solid #2477F6;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
}

.disconnect-btn:hover:not(:disabled) {
  background: #2477F6;
  color: white;
}

.disconnect-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}

.revalidate-btn {
  background: transparent;
  color: #2477F6;
  border: 1px solid #2477F6;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
}

.revalidate-btn:hover:not(:disabled) {
  background: #2477F6;
  color: white;
}

.revalidate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}

.update-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  text-transform: uppercase;
}

.update-btn:hover:not(:disabled) {
  background: #2563eb;
}

.update-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #a0a0a0;
}

/* Search Toggle Styles */
.search-toggle-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.toggle-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.toggle-container input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider-small {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: #cbd5e1;
  border-radius: 24px;
  transition: background-color 0.3s;
  margin-right: 12px;
}

.toggle-slider-small:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  top: 3px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-container input:checked + .toggle-slider-small {
  background-color: #3b82f6;
}

.toggle-container input:checked + .toggle-slider-small:before {
  transform: translateX(20px);
}

.toggle-container input:disabled + .toggle-slider-small {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-text {
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
}

.toggle-hint {
  margin-top: 8px;
  margin-left: 56px;
  font-size: 12px;
  color: #6b7280;
}

/* Subscription Alert Styles */
.subscription-alert {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 30px;
  color: #856404;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.subscription-alert .alert-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.subscription-alert .alert-header i {
  font-size: 20px;
  color: #ff9800;
}

.subscription-alert h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #856404;
}

.subscription-alert p {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  margin-left: 32px;
  color: #856404;
}

.subscription-alert .alert-actions {
  margin-left: 32px;
}

.view-plans-btn {
  background: #3A9BDB;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  outline: none;
}

.view-plans-btn:hover {
  background: #2980b9;
}

.view-plans-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 155, 219, 0.2);
}

.activate-btn {
  margin-top: 12px;
  background: #3A9BDB;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
}

.activate-btn:hover {
  background: #2980b9;
}

.activate-btn i {
  font-size: 14px;
}
</style>